import _ from 'lodash'
import Validators, { required as requiredValidator } from 'redux-form-validators'
import {
  delay,
  scrollToElement,
  dollarFormat,
  getDefaultTimezoneMoment,
  getSecondSanitizedEdgeMoment
} from '../../lib/renderUtil'
import { FORM_ERROR_CLASS_NAME } from '../styles/variables'
import { TOAST_DURATION } from '../constants/proposalTypes'
import { Toast, Modal } from '@vzmi/shp_shared_compo'
import { showOnlyNumber } from './formatter'
import ErrorContent from '../components/Common/ErrorContent/ErrorContent'
import errorHelper from '../../lib/errorHelper'
import { HyperLink } from '../components/Common/Form'
import { API_ERROR_CODE, API_ERROR_MAPPING } from '../constants/globalTypes'

export const FORM_ERROR_CODE = 4226
export const FORM_ERROR_TYPE = 'FORM_ERROR'
export const FORM_ERROR_MESSAGE = '資料不正確，請檢查欄位'
export const API_ERROR_TYPE = 'API_ERROR'

export const required = (options = {}) => (value, values) => {
  return ((Array.isArray(value) || _.isObject(value)) && _.isEmpty(value)) ? (options.msg || options.message || Validators.messages.required) : requiredValidator(options)(value, values)
}

export const findFormErrorElements = parentElement => {
  const selector = !_.isEmpty(parentElement)
    ? `${parentElement} .${FORM_ERROR_CLASS_NAME}`
    : `.${FORM_ERROR_CLASS_NAME}`
  return delay(10)
    .then(
      () => global.document
        ? document.querySelectorAll(selector)
        : []
    )
}

export const scrollToErrorElement = (offset, parentElement) => findFormErrorElements(parentElement)
  .then(elments => scrollToElement(_.get(elments, 0, null), offset))

export const createFormError = (errorObj = {}) => {
  const {
    code = FORM_ERROR_CODE,
    type = FORM_ERROR_TYPE,
    message = FORM_ERROR_MESSAGE,
    result
  } = errorObj

  const error = {
    ...errorObj,
    errorType: type,
    message,
    code,
    type,
    result: errorObj.proposalError
      ? null
      : result
  }
  return error
}

// submit in redux-form returns syncErros object if validation failed, otherwise it should be a promise
export const promisifySubmitResult = submit => event => {
  event && event.persist()
  return findFormErrorElements()
    .then(formErrors => !_.isEmpty(formErrors))
    .then(hasFormErrors => {
      if (hasFormErrors) {
        scrollToErrorElement((window.innerHeight / 2) * -1)
        return Promise.reject(createFormError())
      }

      const result = submit(event)
      if (result && result.then) {
        return result
      } else if (_.isObject(result) && !_.isEmpty(result)) {
        const e = {
          code: FORM_ERROR_CODE,
          message: FORM_ERROR_MESSAGE,
          proposalError: result
        }
        return Promise.reject(createFormError(e))
      } else {
        return Promise.resolve()
      }
    })
}

export const pruneEmpty = (obj) => {
  return (function prune (current) {
    _.forOwn(current, (value, key) => {
      if (_.isNil(value) || _.isNaN(value) ||
        (_.isString(value) && _.isEmpty(value)) ||
        (_.isObject(value) && _.isEmpty(prune(value)))) {
        delete current[key]
      }
    })
    // remove any leftover undefined values from the delete
    // operation on an array
    if (_.isArray(current)) _.pull(current, undefined)

    return current
  }(_.cloneDeep(obj))) // Do not modify the original object, create a clone instead
}

/** 數字加上千分號 */
export const getDollarFormatted = value => dollarFormat(showOnlyNumber(value), '')

export const getProposalError = result => _.get(result, 'proposalError')

export const createErrorToast = ({
  message,
  error,
  duration = TOAST_DURATION
}) => {
  const errorData = errorHelper.extractErrorResult(error)
  const hasMultipleError = _.size(errorData.detail) > 1
  let localMessage = message
  if (hasMultipleError) {
    const multipleError = _.flow(
      _.values,
      _.flattenDeep,
      (errs) => _.map(
        errs,
        (value, key) => {
          return {
            ...value,
            message: `${value.message} (${value.path})`
          }
        })
    )(getProposalError(errorData))

    localMessage = (
      <>
        {message && `${message}&nbsp;`}
        <HyperLink
          onClick={() => Modal.info({
            'data-role': 'proposal-form-error-modal',
            width: 600,
            noIcon: true,
            noButtons: true,
            closable: true,
            centered: true,
            content: <ErrorContent error={multipleError} />
          })}
        >
          錯誤詳情
        </HyperLink>
      </>
    )
  }

  Toast.error(
    <ErrorContent
      message={localMessage}
      error={errorData}
    />,
    { duration }
  )
}

/**
 * handle 送給 API 的 request Promise,
 * 回來的 res 中有 error 的話就 return Promise.reject
 * 不然就 return Promise.resolve(payload)
 *
 * @param {Promise} formRequestPromise
 * @returns {Promise}
 */
export const formRequestHandler = (formRequestPromise) => {
  return new Promise((resolve, reject) => {
    formRequestPromise
      .then(res => {
        const data = _.get(res, 'payload')
        if (res.error) {
          const error = createFormError({
            code: API_ERROR_CODE.GENERAL_ERROR,
            type: API_ERROR_TYPE,
            message: API_ERROR_MAPPING[API_ERROR_CODE.GENERAL_ERROR],
            result: errorHelper.extractErrors(res)
          })

          return reject(error)
        }
        resolve(data)
      })
  })
}

export const updateSyncErrorsByFormError = ({
  transformer,
  formError,
  updateFormSyncErrors
}) => {
  if (!_.isFunction(updateFormSyncErrors)) {
    throw new Error('Unable to update form error without updateFormSyncErrors')
  }
  const { code } = formError
  // if error code matches form error code that means the data can be set to form
  if (code === FORM_ERROR_CODE || code === API_ERROR_CODE.GENERAL_ERROR) {
    let proposalError = getProposalError(errorHelper.extractErrorResult(formError))
    if (!_.isEmpty(proposalError)) {
      if (_.isFunction(transformer)) {
        proposalError = transformer(proposalError)
      }
      // parse and transfer to syncErrors to update redux-from
      const syncErrors = _.reduce(
        proposalError,
        (r, errArr, key) => {
          const errorMsg = _.map(errArr, v => v.message)
          _.set(r, key, errorMsg)
          return r
        }, {})

      if (!_.isEmpty(syncErrors)) {
        // setImmediate to prevent the error to be cleaned
        setImmediate(() => updateFormSyncErrors(syncErrors))
        return syncErrors
      }
    }
  }
}

export const getDefaultListingOffShelvedTs = baseDate => {
  const newMoment = getDefaultTimezoneMoment(baseDate)
    .add(365, 'days')
  return getSecondSanitizedEdgeMoment(newMoment, 'end').format()
}
